import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  FolderIcon,
  FolderOpenIcon,
  DocumentTextIcon,
  UserGroupIcon,
  UserIcon,
  EnvelopeIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  ArchiveBoxIcon,
  PlusIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  TrashIcon,
  Squares2X2Icon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  HomeIcon,
  FunnelIcon,
  TagIcon,
  SparklesIcon,
  CloudArrowUpIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';
import DocumentStorageList from '../components/documentStorage/DocumentStorageList';
import QuickUploadArea from '../components/documentStorage/QuickUploadArea';
import LoadingScreen from '../components/common/LoadingScreen';
import SearchBar from '../components/common/SearchBar';
import TagCloudFilter from '../components/documentStorage/TagCloudFilter';
import { useDocumentFolders } from '../hooks/useDocumentFolders';
import { FolderTreeNode } from '../types/documentStorage';
import CreateFolderModal from '../components/documentStorage/CreateFolderModal';
import MoveFolderModal from '../components/documentStorage/MoveFolderModal';
import DocumentStorageForm from '../components/documentStorage/DocumentStorageForm';
import Modal from '../components/common/Modal';
import ModernDocumentGeneratorModal from '../components/documents/ModernDocumentGeneratorModal';

const DocumentLibrary = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [parentFolderForNew, setParentFolderForNew] = useState<{id: string, name: string} | null>(null);
  const [folderToEdit, setFolderToEdit] = useState<FolderTreeNode | null>(null);
  const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
  const [folderToMove, setFolderToMove] = useState<FolderTreeNode | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<FolderTreeNode | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [folderFilters, setFolderFilters] = useState({
    showCustomerFolders: true,
    showGeneralFolders: true,
    sortBy: 'name' as 'name' | 'date' | 'size'
  });
  const [currentPath, setCurrentPath] = useState<FolderTreeNode[]>([]);
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [tagCloudFilters, setTagCloudFilters] = useState({
    searchQuery: '',
    selectedTags: [] as string[],
    selectedCategories: [] as string[],
    selectedFolders: [] as string[],
    dateRange: 'all',
    fileTypes: [] as string[]
  });
  const [showFolderTagInput, setShowFolderTagInput] = useState<string | null>(null);
  const [newFolderTag, setNewFolderTag] = useState('');
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [showDocumentGenerator, setShowDocumentGenerator] = useState(false);
  
  const {
    folderTree,
    folders,
    loading,
    selectedFolder,
    selectedFolderId,
    expandedFolders,
    toggleFolderExpand,
    selectFolder,
    moveFolder,
    deleteFolder,
    refreshAllFolderStats
  } = useDocumentFolders();

  const handleFolderSelect = (folderId: string) => {
    selectFolder(folderId);
    
    // Auto-expand parent folders in the sidebar
    expandToFolder(folderId);
    
    setIsMobileMenuOpen(false); // Mobil menü bezárása fülváltáskor
  };

  // Function to expand all parent folders to make a specific folder visible
  const expandToFolder = (targetFolderId: string) => {
    const findFolderPath = (nodes: FolderTreeNode[], targetId: string, path: string[] = []): string[] | null => {
      for (const node of nodes) {
        const currentPath = [...path, node.id];
        
        if (node.id === targetId) {
          return currentPath;
        }
        
        if (node.children && node.children.length > 0) {
          const foundPath = findFolderPath(node.children, targetId, currentPath);
          if (foundPath) {
            return foundPath;
          }
        }
      }
      return null;
    };

    const folderPath = findFolderPath(folderTree, targetFolderId);
    if (folderPath) {
      // Expand all parent folders (except the target folder itself)
      const parentsToExpand = folderPath.slice(0, -1);
      
      // Only expand folders that are not already expanded
      parentsToExpand.forEach(folderId => {
        if (!expandedFolders.has(folderId)) {
          toggleFolderExpand(folderId);
        }
      });
    }
  };

  const goToMainView = () => {
    setCurrentPath([]);
    selectFolder(null);
    setIsMobileMenuOpen(false);
  };

  const handleMoveFolder = async (newParentId: string | null) => {
    if (!folderToMove) return;
    
    try {
      await moveFolder(folderToMove.id, newParentId);
      setShowMoveFolderModal(false);
      setFolderToMove(null);
    } catch (error) {
      console.error('Error moving folder:', error);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    
    try {
      await deleteFolder(folderToDelete.id);
      setShowDeleteModal(false);
      setFolderToDelete(null);
      
      // If the deleted folder was selected, clear selection
      if (selectedFolderId === folderToDelete.id) {
        selectFolder(null);
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  const handleAddFolderTag = (folder: FolderTreeNode) => {
    const tag = newFolderTag.trim();
    if (!tag) return;
    
    // Here you would typically update the folder with the new tag
    // For now, just show a success message
    console.log(`Adding tag "${tag}" to folder "${folder.name}"`);
    
    setNewFolderTag('');
    setShowFolderTagInput(null);
  };

  // Get all descendant folder IDs for hierarchical document search
  const getAllDescendantFolderIds = (folder: FolderTreeNode): string[] => {
    const ids = [folder.id];
    
    if (folder.children && folder.children.length > 0) {
      folder.children.forEach(child => {
        ids.push(...getAllDescendantFolderIds(child));
      });
    }
    
    return ids;
  };

  // Folder IDs for document filtering: only documents in the specific folder
  const getFolderIdsForDocuments = (folder: FolderTreeNode): string[] => {
    if (folder.id === 'root') {
      // "Minden dokumentum" mappa esetén minden dokumentumot mutatunk
      return [];
    } else {
      // Konkrét mappa esetén: csak az adott mappa dokumentumai (és almappák dokumentumai)
      return getAllDescendantFolderIds(folder);
    }
  };

  // Check if there are active tag cloud filters
  const hasActiveFilters = (filters?: typeof tagCloudFilters): boolean => {
    if (!filters) return false;
    return (
      filters.searchQuery.length > 0 ||
      filters.selectedTags.length > 0 ||
      filters.selectedCategories.length > 0 ||
      filters.selectedFolders.length > 0 ||
      filters.fileTypes.length > 0 ||
      filters.dateRange !== 'all'
    );
  };

  // Get current level folders based on navigation path
  const getCurrentLevelFolders = (): FolderTreeNode[] => {
    if (currentPath.length === 0) {
      // Root level
      return folderTree;
    } else {
      // Show children of the last folder in path
      const lastFolder = currentPath[currentPath.length - 1];
      return lastFolder.children || [];
    }
  };

  // Navigate into a folder (for main view)
  const navigateIntoFolder = (folder: FolderTreeNode) => {
    if (folder.children && folder.children.length > 0) {
      // Navigate into folder if it has children
      setCurrentPath(prev => [...prev, folder]);
      // Also expand this folder in the sidebar
      if (!expandedFolders.has(folder.id)) {
        toggleFolderExpand(folder.id);
      }
    } else {
      // Select folder for document view if no children
      handleFolderSelect(folder.id);
    }
  };

  // Navigate back in folder hierarchy
  const navigateBack = (targetIndex?: number) => {
    if (targetIndex === undefined) {
      // Go back one level
      setCurrentPath(prev => prev.slice(0, -1));
    } else {
      // Go back to specific level (keep elements up to and including targetIndex)
      setCurrentPath(prev => prev.slice(0, targetIndex + 1));
    }
  };

  // Navigate to a specific folder in breadcrumb path
  const navigateToBreadcrumbFolder = (folderPath: string[], targetIndex: number) => {
    // Find the folder by path
    const findFolderByPath = (nodes: FolderTreeNode[], pathSegments: string[], currentIndex: number = 0): FolderTreeNode | null => {
      if (currentIndex >= pathSegments.length) return null;
      
      const targetName = pathSegments[currentIndex];
      const targetNode = nodes.find(node => node.name === targetName);
      
      if (!targetNode) return null;
      
      // If this is the target index, return this node
      if (currentIndex === targetIndex) {
        return targetNode;
      }
      
      // Otherwise, continue searching in children
      if (targetNode.children && currentIndex < targetIndex) {
        return findFolderByPath(targetNode.children, pathSegments, currentIndex + 1);
      }
      
      return null;
    };

    const targetFolder = findFolderByPath(folderTree, folderPath, 0);
    if (targetFolder) {
      handleFolderSelect(targetFolder.id);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getIconComponent = (iconName: string) => {
    const icons: Record<string, any> = {
      FolderIcon,
      FolderOpenIcon,
      DocumentTextIcon,
      UserGroupIcon,
      UserIcon,
      EnvelopeIcon,
      ReceiptPercentIcon,
      ShieldCheckIcon,
      ArchiveBoxIcon
    };
    return icons[iconName] || FolderIcon;
  };

  const renderFolderTree = (nodes: FolderTreeNode[], level: number = 0) => {
    return nodes.map((folder) => {
      const Icon = getIconComponent(folder.icon);
      const isExpanded = expandedFolders.has(folder.id);
      const isSelected = selectedFolderId === folder.id;
      const hasChildren = folder.children && folder.children.length > 0;

      return (
        <div key={folder.id}>
          <div className="relative">
            <div
              className={`
                w-full flex items-center px-3 py-2 text-xs font-medium rounded-md transition-all duration-150 group relative cursor-pointer
                ${isSelected
                  ? `bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-l-2 border-blue-500 shadow-sm`
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
                }
              `}
              style={{ paddingLeft: `${12 + level * 16}px` }}
              onClick={() => handleFolderSelect(folder.id)}
            >
              {/* Expand/Collapse button */}
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolderExpand(folder.id);
                  }}
                  className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="h-3 w-3" />
                  ) : (
                    <ChevronRightIcon className="h-3 w-3" />
                  )}
                </button>
              )}

              {/* Színes ikon háttér */}
              <div 
                className={`
                  flex items-center justify-center w-7 h-7 rounded-md mr-2.5 transition-all duration-150
                  ${isSelected ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'}
                `}
                style={{ backgroundColor: folder.color + '20' }}
              >
                <Icon 
                  className="h-4 w-4 transition-all duration-150"
                  style={{ color: folder.color }}
                />
              </div>
              
              {/* Mappa tartalom */}
              <div className="text-left flex-1 min-w-0">
                <div className={`font-medium truncate leading-tight ${isSelected ? 'text-current' : ''}`}>
                  {folder.name}
                </div>
                <div className={`text-xs truncate leading-tight transition-colors duration-150 ${
                  isSelected 
                    ? 'text-current opacity-60' 
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                }`}>
                  {folder.documentCount} fájl • {formatFileSize(folder.totalSize)}
                </div>
              </div>
              
              {/* Mappa műveletek */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center space-x-1">
                {/* Tag hozzáadás gomb */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFolderTagInput(showFolderTagInput === folder.id ? null : folder.id);
                  }}
                  className="p-1 hover:bg-purple-200 dark:hover:bg-purple-900/30 rounded"
                  title="Tag hozzáadása"
                >
                  <TagIcon className="h-3 w-3 text-purple-500 dark:text-purple-400" />
                </button>
                
                {/* Áthelyezés gomb */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderToMove(folder);
                    setShowMoveFolderModal(true);
                  }}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Mappa áthelyezése"
                >
                  <ArrowRightIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </button>
                
                {/* Törlés gomb */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderToDelete(folder);
                    setShowDeleteModal(true);
                  }}
                  className="p-1 hover:bg-red-200 dark:hover:bg-red-900/30 rounded"
                  title="Mappa törlése"
                >
                  <TrashIcon className="h-3 w-3 text-red-500 dark:text-red-400" />
                </button>
                
                {/* Almappa létrehozás gomb */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setParentFolderForNew({id: folder.id, name: folder.name});
                    setShowCreateFolderModal(true);
                  }}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Almappa létrehozása"
                >
                  <PlusIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              
              {/* Aktív jelző */}
              {isSelected && (
                <div className="w-0.5 h-6 rounded-full absolute right-1 bg-blue-600" />
              )}
            </div>
          </div>

          {/* Quick Tag Input for Folder */}
          {showFolderTagInput === folder.id && (
            <div className="mx-3 mb-2 p-2 bg-purple-50 dark:bg-purple-900/10 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-center space-x-2">
                <TagIcon className="w-3 h-3 text-purple-600" />
                <input
                  type="text"
                  value={newFolderTag}
                  onChange={(e) => setNewFolderTag(e.target.value)}
                  placeholder="Tag neve..."
                  className="flex-1 px-2 py-1 text-xs border border-purple-300 dark:border-purple-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-purple-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddFolderTag(folder);
                    }
                  }}
                  autoFocus
                />
                <button
                  onClick={() => handleAddFolderTag(folder)}
                  className="px-2 py-1 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors"
                >
                  <PlusIcon className="w-3 h-3" />
                </button>
                <button
                  onClick={() => {
                    setShowFolderTagInput(null);
                    setNewFolderTag('');
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md transition-colors"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Child folders */}
          {hasChildren && isExpanded && folder.children && folder.children.length > 0 && (
            <div className="ml-2">
              {renderFolderTree(folder.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col h-screen">
      <CommonPageHeader
        title="Dokumentumtár"
        subtitle="Ügyfél dokumentumok kezelése és tárolása"
        icon={FolderIcon}
        actionButton={
          <div className="flex items-center gap-2">
            {/* Mobil hamburger menü */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2.5 text-white hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-105"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-5 w-5" />
              ) : (
                <Bars3Icon className="h-5 w-5" />
              )}
            </button>
            
            {/* Gyors navigáció gombok - csak desktop */}
            <Link
              to="/contracts"
              className="hidden lg:flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-xl transition-all duration-200 backdrop-blur-md border border-white/30 hover:scale-105 shadow-md hover:shadow-lg"
              title="Szerződések"
            >
              <DocumentTextIcon className="h-4 w-4" />
              <span>Contracts</span>
            </Link>
            
            <Link
              to="/templates"
              className="hidden lg:flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-xl transition-all duration-200 backdrop-blur-md border border-white/30 hover:scale-105 shadow-md hover:shadow-lg"
              title="Sablonok"
            >
              <DocumentTextIcon className="h-4 w-4" />
              <span>Templates</span>
            </Link>
            
            <Link
              to="/document-variables"
              className="hidden lg:flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-xl transition-all duration-200 backdrop-blur-md border border-white/30 hover:scale-105 shadow-md hover:shadow-lg"
              title="Változók"
            >
              <TagIcon className="h-4 w-4" />
              <span>Variables</span>
            </Link>
            
            {/* Dokumentum generálás gomb - mobil és desktop */}
            <button 
              onClick={() => setShowDocumentGenerator(true)}
              className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 overflow-hidden"
              title="Dokumentum generálás sablonból"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              <SparklesIcon className="h-4 w-4 relative z-10" />
              <span className="hidden md:inline relative z-10">Generálás</span>
            </button>

            {/* Frissítés gomb - mobil és desktop */}
            <button 
              onClick={refreshAllFolderStats}
              className="group flex items-center justify-center p-2.5 md:px-4 md:py-2.5 text-white bg-white/20 hover:bg-white/30 rounded-xl transition-all duration-300 backdrop-blur-md border border-white/30 hover:border-white/50 hover:scale-105 shadow-md hover:shadow-lg"
              title="Mappa statisztikák frissítése"
            >
              <ArrowPathIcon className="h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />
            </button>
          </div>
        }
      />

      {/* Advanced Tag Cloud Filter - Highlighted */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200/50 dark:border-blue-700/50 shadow-sm p-6 backdrop-blur-sm">
          <div className="flex items-center mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center shadow-sm">
                <FunnelIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Intelligens szűrő
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Dokumentumok keresése és szűrése kategóriák, címkék alapján
                </p>
              </div>
            </div>
          </div>
          <TagCloudFilter
            onFilterChange={setTagCloudFilters}
          />
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content - Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        {/* Sidebar - Folder Navigation */}
        <div className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 transition-transform duration-300 ease-in-out
          fixed md:relative z-50 md:z-auto
          w-72 sm:w-80 md:w-64 h-full md:h-auto flex-shrink-0 
          md:bg-transparent bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto
        `}>
          <div className="p-3">
            {/* Mobil bezárás gomb */}
            <div className="md:hidden flex justify-between items-center mb-4 p-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Mappák
              </h3>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Színes fejléc - kattintható */}
            <button
              onClick={goToMainView}
              className="w-full mb-4 p-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-lg text-white shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600"
              title="Vissza a főkönyvtárba"
            >
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-white/20 rounded-md backdrop-blur-sm">
                  <HomeIcon className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-sm">Dokumentumtár</h3>
                  <p className="text-xs text-white/70">Mappák és fájlok • Kattintható</p>
                </div>
              </div>
            </button>

            {/* Folder Tree Navigation */}
            <nav className="space-y-0.5">
              {loading ? (
                <div className="space-y-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="flex items-center px-3 py-2">
                        <div className="w-7 h-7 bg-gray-200 dark:bg-gray-700 rounded-md mr-2.5"></div>
                        <div className="flex-1">
                          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : folderTree.length === 0 ? (
                <div className="text-center py-8 px-3">
                  <FolderIcon className="mx-auto h-8 w-8 text-gray-400 mb-3" />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Nincsenek mappák
                  </p>
                  <button 
                    onClick={() => {
                      setParentFolderForNew(null);
                      setShowCreateFolderModal(true);
                    }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    Első mappa létrehozása
                  </button>
                </div>
              ) : (
                renderFolderTree(folderTree)
              )}
            </nav>
          </div>
          
          {/* Lebegő Új Mappa Gomb - csak desktop-on */}
          <div className="hidden md:block absolute bottom-4 right-4 z-10">
            <button
              onClick={() => {
                setParentFolderForNew(selectedFolder && selectedFolder.id !== 'root' ? {id: selectedFolder.id, name: selectedFolder.name} : null);
                setShowCreateFolderModal(true);
              }}
              className="group relative w-10 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-md hover:shadow-lg transition-colors duration-200"
              title="Új mappa létrehozása"
            >
              <div className="flex items-center justify-center w-full h-full">
                <PlusIcon className="w-5 h-5" />
              </div>
              
              {/* Egyszerű tooltip */}
              <div className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                  Új mappa
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 w-full md:w-auto">
          <div className="p-3">
            {loading ? (
              <LoadingScreen />
            ) : selectedFolder ? (
              <div>
                {/* Folder Header */}
                <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-start md:items-center">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0"
                      style={{ backgroundColor: selectedFolder.color + '20' }}
                    >
                      {React.createElement(getIconComponent(selectedFolder.icon), {
                        className: "w-5 h-5",
                        style: { color: selectedFolder.color }
                      })}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {selectedFolder.name}
                      </h2>
                      {selectedFolder.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {selectedFolder.description}
                        </p>
                      )}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{selectedFolder.documentCount} dokumentum</span>
                        <span className="hidden sm:inline">•</span>
                        <span>{formatFileSize(selectedFolder.totalSize)}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>🌐 0 nyilvános</span>
                        <span className="hidden sm:inline">•</span>
                        <span>🔒 {selectedFolder.documentCount} privát</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Biztonságos dokumentumkezelés</span>
                      </div>
                    </div>
                    {selectedFolder.id !== 'root' && (
                      <button
                        onClick={() => {
                          setFolderToEdit(selectedFolder);
                          setShowCreateFolderModal(true);
                        }}
                        className="ml-3 p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Mappa szerkesztése"
                      >
                        <PencilSquareIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Windows-stílusú megjelenítés: Almappák + Dokumentumok egymás mellett */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Almappák panel */}
                  {selectedFolder.children && selectedFolder.children.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          📁 Almappák ({selectedFolder.children.length})
                        </h3>
                      </div>
                      
                      {viewMode === 'grid' ? (
                        <div className="p-3">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {selectedFolder.children.map((subfolder: FolderTreeNode) => {
                              const Icon = getIconComponent(subfolder.icon);
                              
                              return (
                                <div
                                  key={subfolder.id}
                                  onClick={() => handleFolderSelect(subfolder.id)}
                                  className="group relative cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:shadow-md border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                                >
                                  <div className="text-center space-y-2">
                                    <div 
                                      className="w-10 h-10 mx-auto rounded-lg flex items-center justify-center transition-transform group-hover:scale-105"
                                      style={{ backgroundColor: subfolder.color + '20' }}
                                    >
                                      <Icon 
                                        className="w-5 h-5 transition-all duration-200"
                                        style={{ color: subfolder.color }}
                                      />
                                    </div>
                                    
                                    <div>
                                      <h3 className="font-medium text-xs text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                        {subfolder.name}
                                      </h3>
                                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                        {subfolder.documentCount} dok
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {selectedFolder.children.map((subfolder: FolderTreeNode) => {
                            const Icon = getIconComponent(subfolder.icon);
                            
                            return (
                              <div
                                key={subfolder.id}
                                onClick={() => handleFolderSelect(subfolder.id)}
                                className="group cursor-pointer px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                              >
                                <div className="flex items-center space-x-2">
                                  <div 
                                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: subfolder.color + '20' }}
                                  >
                                    <Icon 
                                      className="w-4 h-4"
                                      style={{ color: subfolder.color }}
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                      {subfolder.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {subfolder.documentCount} dok • {formatFileSize(subfolder.totalSize)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Dokumentumok panel */}
                  <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden ${
                    !selectedFolder.children || selectedFolder.children.length === 0 ? 'lg:col-span-2' : ''
                  }`}>
                    <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                          Dokumentumok ({selectedFolder.documentCount})
                        </h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => {
                              setParentFolderForNew({id: selectedFolder.id, name: selectedFolder.name});
                              setShowCreateFolderModal(true);
                            }}
                            className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                          >
                            <FolderIcon className="h-3.5 w-3.5 mr-1" />
                            <span className="hidden sm:inline">Új mappa</span>
                            <span className="sm:hidden">📁</span>
                          </button>
                          <button
                            onClick={() => setShowDocumentForm(true)}
                            className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                          >
                            <DocumentTextIcon className="h-3.5 w-3.5 mr-1" />
                            <span className="hidden sm:inline">Új dokumentum</span>
                            <span className="sm:hidden">📄</span>
                          </button>
                          <button
                            onClick={() => setShowUploadArea(!showUploadArea)}
                            className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105 ${
                              showUploadArea 
                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            <CloudArrowUpIcon className="h-3.5 w-3.5 mr-1" />
                            <span className="hidden sm:inline">{showUploadArea ? 'Bezárás' : 'Feltöltés'}</span>
                            <span className="sm:hidden">↑</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Drag & Drop Upload Area */}
                    {showUploadArea && (
                      <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                        <QuickUploadArea 
                          folderId={selectedFolder.id === 'root' ? undefined : selectedFolder.id}
                          customerId={selectedFolder.customerId}
                          onUploadComplete={() => {
                            refreshAllFolderStats();
                          }}
                          onClose={() => setShowUploadArea(false)}
                        />
                      </div>
                    )}
                    
                    <div className="p-4">
                      <DocumentStorageList 
                        customerId={selectedFolder.customerId}
                        folderId={hasActiveFilters(tagCloudFilters) ? undefined : (selectedFolder.id === 'root' ? undefined : selectedFolder.id)}
                        folderIds={undefined}
                        showCustomerFilter={!selectedFolder.customerId}
                        onDocumentChange={refreshAllFolderStats}
                        viewMode={viewMode}
                        tagCloudFilters={tagCloudFilters}
                        status={hasActiveFilters(tagCloudFilters) ? undefined : (selectedFolder.name?.toLowerCase().includes('archív') ? 'archived' : 'active')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {/* Mappák és dokumentumok megjelenítése - Windows stílusban */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Mappák panel */}
                  {getCurrentLevelFolders().length === 0 ? (
                    <div className="lg:col-span-2">
                      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="max-w-md mx-auto">
                          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center">
                            <FolderIcon className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Üres dokumentumtár
                    </h3>
                          <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
                            A dokumentumtár teljesen üres. Hozza létre az első mappát a dokumentumok 
                            professzionális rendszerezéséhez.
                    </p>
                      <button 
                        onClick={() => {
                          setParentFolderForNew(null);
                          setShowCreateFolderModal(true);
                        }}
                            className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                      >
                            <PlusIcon className="h-5 w-5 mr-2" />
                        Első mappa létrehozása
                      </button>
                      </div>
                    </div>
                    </div>
                  ) : (
                    <>
                      {/* Mappák */}
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            📁 Mappák ({getCurrentLevelFolders().length})
                          </h3>
                        </div>
                    {viewMode === 'grid' ? (
                      /* Grid nézet - mappák mint fájlkezelőben */
                      <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                          {getCurrentLevelFolders().map((folder: FolderTreeNode) => {
                            const Icon = getIconComponent(folder.icon);
                            
                            return (
                              <div
                                key={folder.id}
                                onDoubleClick={() => navigateIntoFolder(folder)}
                                onClick={() => handleFolderSelect(folder.id)}
                                className="group relative cursor-pointer p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:shadow-md border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                              >
                                <div className="text-center space-y-3">
                                  {/* Mappa ikon */}
                                  <div 
                                    className="w-16 h-16 mx-auto rounded-xl flex items-center justify-center transition-transform group-hover:scale-105"
                                    style={{ backgroundColor: folder.color + '20' }}
                                  >
                                    <Icon 
                                      className="w-8 h-8 transition-all duration-200"
                                      style={{ color: folder.color }}
                                    />
              </div>
                                  
                                  {/* Mappa név */}
                                  <div>
                                    <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                      {folder.name}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {folder.documentCount} fájl
                                      {folder.children && folder.children.length > 0 && (
                                        <span> • {folder.children.length} almappa</span>
                                      )}
                                    </p>
          </div>
                                  
                                  {/* Ügyfél badge */}
                                  {folder.customerId && (
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                      <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full">
                                        <UserIcon className="w-3 h-3 mr-1" />
                                        Ügyfél
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Hover műveletek */}
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="flex items-center space-x-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setParentFolderForNew({id: folder.id, name: folder.name});
                                        setShowCreateFolderModal(true);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                      title="Almappa létrehozása"
                                    >
                                      <PlusIcon className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFolderToMove(folder);
                                        setShowMoveFolderModal(true);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                      title="Áthelyezés"
                                    >
                                      <ArrowRightIcon className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFolderToDelete(folder);
                                        setShowDeleteModal(true);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      title="Törlés"
                                    >
                                      <TrashIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      /* Lista nézet */
                      <div>
                        {/* Táblázat fejléc */}
                        <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <div className="col-span-5">Név</div>
                            <div className="col-span-2">Dok</div>
                            <div className="col-span-2">Méret</div>
                            <div className="col-span-2">Ügyfél</div>
                            <div className="col-span-1 text-right">Opciók</div>
                          </div>
                        </div>
                        
                        {/* Mappa sorok */}
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {getCurrentLevelFolders().map((folder: FolderTreeNode) => {
                            const Icon = getIconComponent(folder.icon);
                            
                            return (
                              <div
                                key={folder.id}
                                onDoubleClick={() => navigateIntoFolder(folder)}
                                onClick={() => handleFolderSelect(folder.id)}
                                className="group cursor-pointer px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                              >
                                <div className="grid grid-cols-12 gap-2 items-center">
                                  {/* Név */}
                                  <div className="col-span-5 flex items-center space-x-2 min-w-0">
                                    <div 
                                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: folder.color + '20' }}
                                    >
                                      <Icon 
                                        className="w-4 h-4"
                                        style={{ color: folder.color }}
                                      />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                        {folder.name}
                                      </p>
                                      {folder.description && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                          {folder.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Dokumentumok */}
                                  <div className="col-span-2">
                                    <span className="text-xs text-gray-900 dark:text-gray-100">
                                      {folder.documentCount}
                                    </span>
                                  </div>
                                  
                                  {/* Méret */}
                                  <div className="col-span-2">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {formatFileSize(folder.totalSize)}
                                    </span>
                                  </div>
                                  
                                  {/* Ügyfél */}
                                  <div className="col-span-2">
                                    {folder.customerName ? (
                                      <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full">
                                        <UserIcon className="w-3 h-3 mr-1" />
                                        {folder.customerName.length > 15 ? folder.customerName.substring(0, 15) + '...' : folder.customerName}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-gray-400">Általános</span>
                                    )}
                                  </div>
                                  
                                  {/* Műveletek */}
                                  <div className="col-span-1">
                                    {/* Jelzés hogy vannak műveletek */}
                                    <div className="group-hover:hidden flex items-center justify-center">
                                      <div className="flex items-center gap-0.5 text-gray-400 dark:text-gray-500">
                                        <div className="w-1 h-1 rounded-full bg-current"></div>
                                        <div className="w-1 h-1 rounded-full bg-current"></div>
                                        <div className="w-1 h-1 rounded-full bg-current"></div>
                                      </div>
                                    </div>
                                    {/* Műveletek hover-re */}
                                    <div className="hidden group-hover:block">
                                      <div className="flex items-center gap-0.5 justify-end">
                                        {folder.id !== 'root' && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setFolderToEdit(folder);
                                              setShowCreateFolderModal(true);
                                            }}
                                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                            title="Szerkesztés"
                                          >
                                            <PencilSquareIcon className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setParentFolderForNew({id: folder.id, name: folder.name});
                                            setShowCreateFolderModal(true);
                                          }}
                                          className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                          title="Almappa"
                                        >
                                          <PlusIcon className="w-3.5 h-3.5" />
                                        </button>
                                        {folder.id !== 'root' && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setFolderToDelete(folder);
                                              setShowDeleteModal(true);
                                            }}
                                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                            title="Törlés"
                                          >
                                            <TrashIcon className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                      </div>
                      
                      {/* Dokumentumok panel - gyökér dokumentumok */}
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                              📄 Gyökér dokumentumok
                            </h3>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => {
                                  setParentFolderForNew(null);
                                  setShowCreateFolderModal(true);
                                }}
                                className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                              >
                                <FolderIcon className="h-3.5 w-3.5 mr-1" />
                                <span className="hidden sm:inline">Új mappa</span>
                                <span className="sm:hidden">📁</span>
                              </button>
                              <button
                                onClick={() => setShowDocumentForm(true)}
                                className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                              >
                                <DocumentTextIcon className="h-3.5 w-3.5 mr-1" />
                                <span className="hidden sm:inline">Új dokumentum</span>
                                <span className="sm:hidden">📄</span>
                              </button>
                              <button
                                onClick={() => setShowUploadArea(!showUploadArea)}
                                className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105 ${
                                  showUploadArea 
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                              >
                                <CloudArrowUpIcon className="h-3.5 w-3.5 mr-1" />
                                <span className="hidden sm:inline">{showUploadArea ? 'Bezárás' : 'Feltöltés'}</span>
                                <span className="sm:hidden">↑</span>
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Drag & Drop Upload Area */}
                        {showUploadArea && (
                          <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                            <QuickUploadArea 
                              folderId={undefined} // gyökér szint
                              customerId={undefined}
                              onUploadComplete={() => {
                                refreshAllFolderStats();
                              }}
                              onClose={() => setShowUploadArea(false)}
                            />
                          </div>
                        )}
                        
                        <div className="p-4">
                          <DocumentStorageList 
                            customerId={undefined}
                            folderId={undefined}
                            folderIds={undefined}
                            showCustomerFilter={true}
                            onDocumentChange={refreshAllFolderStats}
                            viewMode={viewMode}
                            tagCloudFilters={tagCloudFilters}
                            status={hasActiveFilters(tagCloudFilters) ? undefined : "active"}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => {
          setShowCreateFolderModal(false);
          setParentFolderForNew(null);
          setFolderToEdit(null);
        }}
        parentFolderId={parentFolderForNew?.id}
        folderToEdit={folderToEdit}
        parentFolderName={parentFolderForNew?.name}
      />

      {/* Move Folder Modal */}
      <MoveFolderModal
        isOpen={showMoveFolderModal}
        onClose={() => {
          setShowMoveFolderModal(false);
          setFolderToMove(null);
        }}
        onMove={handleMoveFolder}
        currentFolder={folderToMove}
        allFolders={folderTree}
      />

      {/* Delete Folder Confirmation Modal */}
      {folderToDelete && (
        <div className="fixed inset-0 bg-black/25 dark:bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Mappa törlése
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Ez a művelet nem visszavonható
                </p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div 
                  className="flex items-center justify-center w-8 h-8 rounded mr-3"
                  style={{ backgroundColor: folderToDelete.color + '20' }}
                >
                  <FolderIcon 
                    className="h-5 w-5"
                    style={{ color: folderToDelete.color }}
                  />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {folderToDelete.name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {folderToDelete.documentCount} dokumentum • {formatFileSize(folderToDelete.totalSize)}
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                Biztosan törölni szeretné ezt a mappát? A mappában lévő dokumentumok nem lesznek törölve, 
                de elveszítik a mappa kapcsolatukat.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setFolderToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Mégse
              </button>
              <button
                onClick={handleDeleteFolder}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Törlés
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Form Modal */}
      <Modal 
        isOpen={showDocumentForm} 
        onClose={() => setShowDocumentForm(false)} 
        title="Új dokumentum létrehozása"
        size="4xl"
      >
        <DocumentStorageForm
          document={null}
          onClose={() => {
            setShowDocumentForm(false);
            refreshAllFolderStats();
          }}
        />
      </Modal>

      {/* Document Generator Modal */}
      {showDocumentGenerator && (
        <ModernDocumentGeneratorModal
          isOpen={showDocumentGenerator}
          onClose={() => {
            setShowDocumentGenerator(false);
            refreshAllFolderStats(); // Frissítjük a dokumentumlistát a generálás után
          }}
          documentDataProvider={null} // Nincs előre kiválasztott ügyfél
        />
      )}
    </div>
  );
};

export default DocumentLibrary;
